import merge from 'lodash/merge';
import { getStandardToolDomains } from '../../api/platformConfig';
import { getShopifyConfigs } from '../../api/orders/shopify';
import { clearOrderSearchState } from './OrdersSearchActions';
import { clearReturnSearchState } from './CommonInterface/Returns/ReturnActions';
import { getHelpCenterCategories, getHelpCenterSections, clearZendesk, clearPartnerTags } from './ZendeskActions';
import { fetchDynamicIntegrations } from '../../api/dynamicIntegrations';
import { rbiClearState } from './RBIActions';
import { extractSelectedTicketFromState, getIsMacroWizardEmpty } from '../../extractFromState';
import { loadMacrosAndDecisionTreeForAccount, loadBusinessAlertsForAccount, setDecisionTrees } from '../searchActions';
import { hydrateHistoricalTickets } from '../ticketActions';
import { setTaggingActive, setCenterPaneExtraInfoPanelActive } from '../index';
import * as searchTypes from '../../constants/searchTypes';
import { SET_SEARCH_TYPE, SET_RETURN_LABEL_LINK } from '../../constants/ordersSearchActionTypes';
import {
  SET_ALERT_MANAGER_TYPE,
  SET_BLOCKING_ALERT_VIEWED,
  CLEAR_BLOCKING_ALERT_VIEWED_IDS,
  CLEAR_SEARCH,
  SET_INTEGRATION_OPTIONS,
} from 'actions/types';
import { setEscalationMessage, setOutboundEscalationObject } from '../index';
import { handleError } from 'services/ErrorService/ErrorService';
import { clearCustomerSearch } from './GladlyActions';
import { clearAccountSearch } from './DecisionTree/accountSearchActions';

const SUPPORTED_STANDARD_TOOLS = [
  'zendesk',
  'amazon',
  'messenger',
  'gorgias',
  'intercom',
  'customseller',
  'shipstation',
  'simplrorder',
  'rbi',
  'salesforceConfig',
  'bigCommerce',
  'revolve',
  'common',
  'commonReturns',
  'commonSubscription',
];

const clearSearch = () => ({
  type: CLEAR_SEARCH,
});

export const setSelectedTool = (toolName) => {
  return (dispatch, getState) => {
    // if we're selecting Shopify, or something other than Zendesk, just dispatch the simple action
    if (toolName !== 'ZENDESK') {
      return dispatch({
        type: 'SELECTED_TOOL',
        name: toolName,
      });
    }

    const ticket = extractSelectedTicketFromState(getState());

    // if we're selecting Zendesk, may need to retrieve categories and sections first
    const zendesk = getState().tools.zendesk;
    const selectedToolDomains = getState().tools.general.selectedToolDomains;
    const needCategories = selectedToolDomains.zendesk && !(zendesk.categories && zendesk.categories.length);
    const needSections = selectedToolDomains.zendesk && !(zendesk.sections && zendesk.sections.length);
    const simplrAccountId = ticket && ticket.simplrAccountPtrId;

    if (needCategories) {
      dispatch(getHelpCenterCategories(selectedToolDomains.zendesk, simplrAccountId));
    }
    if (needSections) {
      dispatch(getHelpCenterSections(selectedToolDomains.zendesk, simplrAccountId));
    }
    dispatch({
      type: 'SELECTED_TOOL',
      name: toolName,
    });
  };
};

/**
 * Fetches data for active dynamic integrations
 * We use this data to determine which COI search options are active
 *
 * @param {object} params
 * @param {string} params.simplrAccountId
 * @param {string=} params.brand
 *
 * @returns {Promise}
 */
export const loadDynamicIntegrations = (params) => {
  return async (dispatch) => {
    try {
      const { simplrAccountId } = params;
      const { data } = await fetchDynamicIntegrations(params);
      dispatch({
        type: SET_INTEGRATION_OPTIONS,
        simplrAccountId,
        data,
      });
    } catch (error) {
      handleError({ error, customMessage: 'Error getting integration data' });
    }
  };
};

/**
 * Fetches partner data for legacy platform config-style integrations
 *
 * @param {string} simplrAccountId
 * @param {boolean=} getFromBackend
 *
 * @returns {Promise}
 */
export const loadECommerceIntegrations = (simplrAccountId, getFromBackend = false) => {
  return async (dispatch, getState) => {
    const state = getState();
    const general = state.tools.general;
    const toolDomains = general.toolDomains;

    // if we've already retrieved the tool domains for this account, return them directly
    const existingToolDomains = toolDomains[simplrAccountId];
    if (existingToolDomains && !getFromBackend) {
      dispatch({
        type: 'SELECT_TOOL_DOMAINS',
        domains: existingToolDomains,
      });
      await dispatch(selectActionTab());

      return existingToolDomains;
    }

    try {
      // otherwise, fetch them from the backend
      let [accountToolDomains, accountShopifyConfigs] = await Promise.all([
        getStandardToolDomains(simplrAccountId),
        getShopifyConfigs(simplrAccountId),
        dispatch(selectActionTab()),
      ]);

      accountToolDomains = accountToolDomains || {};
      accountShopifyConfigs = accountShopifyConfigs || {};
      const domains = {};
      const configs = {};

      for (let tool of SUPPORTED_STANDARD_TOOLS) {
        domains[tool] = accountToolDomains[tool];
        configs[tool + 'Config'] = accountToolDomains[tool + 'Config'];
      }

      domains['shopify'] = Object.keys(accountShopifyConfigs);
      configs['shopifyConfigs'] = accountShopifyConfigs;

      const mergedDomains = merge(domains, configs);

      dispatch({
        type: 'ADD_AND_SELECT_TOOL_DOMAINS',
        account: simplrAccountId,
        domains: mergedDomains,
      });
    } catch (error) {
      handleError({ error, customMessage: 'Error getting tool domains for current user' });

      return null;
    }
  };
};

export const selectActionTab = () => async (dispatch, getState) => {
  await Promise.all([dispatch(loadMacrosAndDecisionTreeForAccount()), dispatch(loadBusinessAlertsForAccount())]);

  // if macro wizard is not empty, use that by default
  const searchType = getIsMacroWizardEmpty(getState()) ? searchTypes.SEARCH_ALL : searchTypes.MACRO_WIZARD;
  dispatch(setSearchType(searchType));
};

export const setSearchType = (searchType) => ({
  type: SET_SEARCH_TYPE,
  searchType: searchType,
});

export const setAlertManagerType = (alertType) => ({
  type: SET_ALERT_MANAGER_TYPE,
  alertType,
});

export const setBlockingAlertViewed = (id) => ({
  type: SET_BLOCKING_ALERT_VIEWED,
  id,
});

export const clearBlockingAlertViewedIds = () => ({
  type: CLEAR_BLOCKING_ALERT_VIEWED_IDS,
});

export const deactivateHistoricalTicket = () => ({
  type: 'DEACTIVATE_HISTORICAL_TICKET',
});

export const activateHistoricalTicket = () => ({
  type: 'ACTIVATE_HISTORICAL_TICKET',
});

const clearECommerceSearchState = () => (dispatch) => {
  dispatch(clearOrderSearchState());
  dispatch(clearReturnSearchState());
  dispatch(rbiClearState());
};

export const clearTools = () => {
  return (dispatch) => {
    dispatch(setDecisionTrees({}, null));
    dispatch(setCenterPaneExtraInfoPanelActive(false, null));
    dispatch(setEscalationMessage(''));
    dispatch(setOutboundEscalationObject({ outboundEscalationMessage: '' }));
    dispatch(setTaggingActive(false));
    dispatch(deactivateHistoricalTicket());
    dispatch(clearECommerceSearchState());
    dispatch(clearZendesk());
    dispatch(clearPartnerTags());
    dispatch(setAlertManagerType(null));
    dispatch(clearSearch());
    dispatch({
      type: SET_SEARCH_TYPE,
      searchType: null,
    });
    dispatch({
      type: 'SELECTED_TOOL',
      name: 'ACTIONS',
    });
    dispatch(
      hydrateHistoricalTickets({
        historicalTicketsTotalCount: 0,
        allTickets: [],
      })
    );
    dispatch(clearAccountSearch());
    dispatch(clearCustomerSearch());
  };
};

export const setReturnLabelLink = (link) => ({
  type: SET_RETURN_LABEL_LINK,
  link,
});

export const setNewAddress = (address) => ({
  type: 'SET_NEW_ADDRESS',
  payload: address,
});
